<script lang="ts">
import { defineComponent, PropType } from 'vue'
import SolutionName from '@/components/solutions/SolutionName.vue'
import ExternalHtmlWrapper from '@/components/_core/ExternalHtmlWrapper.vue'
import solutionCategory from '@/models/solutionCategory'

export default defineComponent({
  name: 'SolutionContentsIntro',

  components: {
    SolutionName,
    ExternalHtmlWrapper,
  },

  props: {
    item: {
      type: Object as PropType<SolutionContentItem>,
      required: true,
    },

    /**
     * テキスト見出しを表示するかどうか
     * @description 表示しない場合、h1 は画像が割り当てられる（レイアウト上の表示位置等は変わらず、あくまでマークアップ構造上の問題）
     */
    hasHeading: {
      type: Boolean,
      default: false,
    },

    /**
     * 説明欄の見出し等の色合いを、デフォルトではなくブランド独自のカラーにしたい場合の設定
     *
     * @description primary、secondary 双方に「#」から始まる 16 進数のカラーコードを指定
     */
    brandColor: {
      type: Object as PropType<{
        primary: string,
        secondary: string,
      }>,
      default: () => ({ primary: '', secondary: '' }),
      validator(value: {
        primary: string,
        secondary: string,
      }) {
        // カラーコード未指定か、#RRGGBB の形式で指定されていれば true
        const isValidColor = (value: string) => {
          return value === '' || /^#([0-9a-fA-F]{6})$/.test(value)
        }

        // #RRGGBB の形式か判定
        return isValidColor(value.primary) && isValidColor(value.secondary)
      },
    },
  },

  setup() {
    const { getSolutionCategoryLabel } = solutionCategory()

    return {
      // methods
      getSolutionCategoryLabel,
    }
  },
})
</script>

<template>
  <div class="l-unit__solutionContentsCard">
    <div
      class="l-unit__solutionContentsCard--header"
      :data-hero="!!item.thumbImageUrl && !hasHeading">
      <div
        v-if="hasHeading || !item.thumbImageUrl"
        class="o-heading__solutionContentsCard">
        <h1 class="o-heading__solutionContentsCard--item">
          {{ item.title }}
        </h1>
      <!-- /.o-heading__solutionContentsCard --></div>
      <h1
        v-else
        class="h1">
        <slot name="thumbImage">
          <img
            class="img"
            :src="item.thumbImageUrl"
            fetchpriority="high"
            alt=""
            data-is-liquid="true">
        </slot>
      </h1>
    <!-- /.l-unit__solutionContentsCard header --></div>

    <div
      v-if="hasHeading && (item.thumbImageUrl || $slots.thumbImage)"
      class="l-unit__solutionContentsCard--image">
      <slot name="thumbImage">
        <img
          class="img"
          :src="item.thumbImageUrl"
          fetchpriority="high"
          alt=""
          data-is-liquid="true">
      </slot>
    <!-- /.l-unit__solutionContentsCard image --></div>

    <div
      v-if="item.description"
      class="l-unit__solutionContentsCard--description">
      <div class="o-label__description">
        <p
          class="o-label__description--item"
          v-html="item.description" />
      <!-- /.o-label__description --></div>
    <!-- /.l-unit__solutionContentsCard description --></div>

    <div class="l-unit__solutionContentsCard--description is-introduction">
      <slot name="body">
        <ExternalHtmlWrapper
          v-if="item.introduction"
          :contents-html="item.introduction"
          :color-palette="brandColor"
          page-type="solution" />
      </slot>
    <!-- /.l-unit__solutionContentsCard description --></div>

    <div
      v-if="item.solution?.categoryIds?.length"
      class="l-unit__solutionContentsCard--footer is-category">
      <div class="o-list__category">
        <ul class="o-list__category--items">
          <li
            v-for="id in item.solution.categoryIds"
            :key="id"
            class="o-list__category--item">
            <div
              class="o-label__category"
              :style="{
                '--font-color': brandColor.primary,
              }">
              <p class="o-label__category--item">
                {{ getSolutionCategoryLabel(id) }}
              </p>
            <!-- /.o-label__category --></div>
          </li>
        </ul>
      <!-- /.o-list__category --></div>
    <!-- /.l-unit__solutionContentsCard footer --></div>

    <div
      v-if="item.solution"
      class="l-unit__solutionContentsCard--footer is-name">
      <SolutionName
        :solution="item.solution"
        :has-link="true"
        :is-truncate="false"
        size="normal" />
    <!-- /.l-unit__solutionContentsCard footer --></div>
  <!-- /.l-unit__solutionContentsCard --></div>
</template>

<style lang="sass" scoped>
// layout
.l-unit__solutionContentsCard
  $root: &
  +base-layout($image-position: top, $root: $root)
  height: auto
  min-height: 0
  flex-direction: row
  flex-wrap: wrap
  text-decoration: none
  margin-top: 1em
  +transition

  &:first-child
    margin-top: 0

  & &--header,
  & &--image,
  & &--description,
  & &--footer
    width: 100%

  & &--header
    flex-grow: 0
    order: -1
    margin-bottom: 0.6em

    &:first-child:last-child
      margin-bottom: 0

  & &--image
    order: -2
    margin-bottom: 0.6em

  & &--description
    flex-grow: 1
    order: 1
    margin-top: 1.5em

  & &--footer
    &.is-category
      order: -1
      margin-bottom: 0.8em

      &:last-child
        margin-bottom: 0

    &.is-name,
    &.is-date
      width: auto
      line-height: 1

    &.is-date
      margin-left: 1.4em

// object
.o-heading__solutionContentsCard
  $root: &

  & &--item
    color: $font-black
    font:
      size: $font-large
      weight: bold

  +breakpoint
    #{$root}--item
      line-height: 1.4
      font-size: $font-normal

.o-list__category
  $root: &
  $gutter-size: 0.4em
  margin-top: 1em

  &:first-child
    margin-top: 0

  & &--items
    display: flex
    flex-wrap: wrap
    margin:
      top: -$gutter-size
      left: -$gutter-size

  & &--item
    margin:
      top: $gutter-size
      left: $gutter-size

.o-label__category
  & &--item
    display: inline-block
    line-height: 1.7
    color: var(--font-color, $font-orange)
    font-size: $font-x-small
    padding: 0 0.5em
    background-color: rgba($bg-white, 1)
    border: 1px solid var(--font-color, $border-orange)
    border-radius: $border-radius

.o-label__description
  & &--item
    color: $font-light-gray
    font-size: $font-small
</style>
